import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { CustomerDTO, RfqDTO, UserType } from '@luminovo/api-contracts';
import { id, isProductionEnvironment } from '@luminovo/commons';
import {
    type AppLinkOptions,
    colorSystem,
    Flexbox,
    linkable,
    linkOptions,
    Tag,
    useCanOpen,
    useIsRouteActive,
} from '@luminovo/design-system';
import { CustomerLabel, RfqLabel } from '@luminovo/manufacturing-core';
import { Box, styled, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
import { HouseIcon } from 'lucide-react';
import * as React from 'react';
import type { JSX } from 'react';

import { StyledRfqStatusBox } from '@/areas/projects/common/RfqStatusBox';
import { useCustomer } from '@/domain/customer/customerHandler';
import { useRfQ } from '@/domain/projects/rfqHandler';
import { useCurrentUserDetailsContext } from '@/shared/data/contexts/CurrentUserDetailsContext';
import { isCustomerRfQDisabled, useFeatureFlag } from '@/shared/permissions/featureFlags';
import { Protected } from '@/shared/permissions/Protected';

import { GlobalCommandPalette } from '../GlobalCommandPalette';
import { templateLinks } from '../navigation/templateLinks';
import { Avatar } from './Avatar';
import { useCustomFavicon } from './hooks/useCustomFavicon';
import { useCustomTitle } from './hooks/useCustomTitle';
import { NavbarIconDark, NavbarIconLight } from './NavbarIcon';

const LazyNotificationsButton = React.lazy(async () => {
    const { NotificationsButton } = await import('./NotificationsButton');
    return { default: NotificationsButton };
});

/** Non-critical: if MagicBell fails, the bell disappears (reported to Sentry) instead of crashing the navbar. */
export const NavbarNotifications = ({
    variant,
    rfqId,
    renderTrigger,
}: {
    variant: 'light' | 'dark';
    /** Scopes the bell to one project. Absent outside a project. */
    rfqId?: string;
    /** A trigger of the caller's own in place of the bell, such as the new sidebar's row (`NotificationsButton`). */
    renderTrigger?: React.ComponentProps<typeof LazyNotificationsButton>['renderTrigger'];
}): JSX.Element => (
    <Sentry.ErrorBoundary fallback={<></>}>
        <React.Suspense fallback={null}>
            <LazyNotificationsButton variant={variant} rfqId={rfqId} renderTrigger={renderTrigger} />
        </React.Suspense>
    </Sentry.ErrorBoundary>
);

const navbarHeight = '48px';

const Filler = (): JSX.Element => (
    <Box
        sx={{
            flexGrow: 1,
        }}
    />
);

const NavbarContainer: React.FunctionComponent<React.PropsWithChildren<{}>> = (props) => {
    return <>{props.children}</>;
};

const NavDark = styled('nav')({
    padding: '0 16px',
    width: '100vw',
    maxWidth: '100vw',
    display: 'flex',
    height: navbarHeight,
    minHeight: navbarHeight,
    flexDirection: 'row',
    boxSizing: 'border-box',
    background: colorSystem.neutral[9],
    alignItems: 'center',
    '& > a + a': {
        marginRight: 12,
        whiteSpace: 'nowrap',
    },
});

const NavLight = styled(NavDark)({
    background: colorSystem.neutral.white,
    borderBottom: `1px solid ${colorSystem.neutral[2]}`,
});

const StyledLinkDark = styled('a')({
    color: colorSystem.neutral[6],
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: '0 12px',
    textDecoration: 'none',
    '&:hover': {
        color: colorSystem.neutral[1],
        transition: 'all 0.5s ease',
    },
    '&[aria-current="page"]': {
        color: colorSystem.neutral[1],
        background: colorSystem.neutral[8],
    },
});

const StyledLinkLight = styled(StyledLinkDark)({
    color: colorSystem.neutral[6],
    '&:hover': {
        color: colorSystem.primary[7],
        transition: 'all 0.5s ease',
    },
    '&[aria-current="page"]': {
        color: colorSystem.primary[7],
        background: colorSystem.primary[1],
        borderBottom: `2px solid ${colorSystem.primary[7]}`,
    },
});

type NavbarVariant = 'dark' | 'light';

/** Sets `aria-current` from `isCurrent`: the router marks only a link's own route, and entries stand for more. */
const NavbarAnchorHost = React.forwardRef<
    HTMLAnchorElement,
    Omit<React.ComponentPropsWithoutRef<'a'>, 'aria-current'> & { variant: NavbarVariant; isCurrent: boolean }
>(function NavbarAnchorHost({ variant, isCurrent, children, ...props }, ref) {
    const Anchor = variant === 'dark' ? StyledLinkDark : StyledLinkLight;
    return (
        <Anchor ref={ref} {...props} aria-current={isCurrent ? 'page' : undefined}>
            <Typography variant="h4" component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                {children}
            </Typography>
        </Anchor>
    );
});

const NavbarAnchor = linkable(NavbarAnchorHost);

/** The logo in the corner: an unstyled anchor to the start page. */
const LogoLink = linkable(styled('a')({}));

/** Where the home navbar's entries go. */
const homeLinks = {
    projects: linkOptions({ to: '/projects' }),
    assemblies: linkOptions({ to: '/assemblies' }),
    orders: linkOptions({ to: '/orders/{-$orderId}', params: { orderId: undefined } }),
    parts: linkOptions({ to: '/parts/search' }),
    risk: linkOptions({ to: '/risk-management' }),
    customers: linkOptions({ to: '/customers' }),
    manufacturers: linkOptions({ to: '/manufacturers' }),
    suppliers: linkOptions({ to: '/suppliers' }),
    purchasing: linkOptions({ to: '/purchase-orders' }),
    negotiations: linkOptions({ to: '/negotiations' }),
    insights: linkOptions({ to: '/monitoring' }),
    supplierPortal: linkOptions({ to: '/supplier-portal' }),
};

/** Where the project navbar's entries go, for one project. */
function projectLinks(projectId: string) {
    return {
        dashboard: linkOptions({ to: '/projects/$projectId/dashboard', params: { projectId } }),
        design: linkOptions({ to: '/projects/$projectId/bom', params: { projectId } }),
        sourcing: linkOptions({ to: '/projects/$projectId/sourcing', params: { projectId } }),
        manufacturing: linkOptions({
            to: '/projects/$projectId/manufacturing',
            params: { projectId },
            search: { forwardIfSingleAssembly: true },
        }),
        calculation: linkOptions({ to: '/projects/$projectId/calculation', params: { projectId } }),
        flexibleCalculation: linkOptions({ to: '/projects/$projectId/flexible-calculation', params: { projectId } }),
        quotation: linkOptions({ to: '/projects/$projectId/quotation', params: { projectId } }),
    };
}

type HomeLinks = typeof homeLinks;
type ProjectLinks = ReturnType<typeof projectLinks>;
type TemplateLinks = typeof templateLinks;
type NavbarLinkOptions =
    | HomeLinks[keyof HomeLinks]
    | ProjectLinks[keyof ProjectLinks]
    | TemplateLinks[keyof TemplateLinks];

/**
 * Current on its page and the pages beneath it, search ignored; `isCurrent` overrides that. Renders nothing where the
 * page refuses the user, so an entry needs no check of its own.
 */
const NavbarLink = React.forwardRef<
    HTMLAnchorElement,
    React.PropsWithChildren<{
        id?: string;
        link: NavbarLinkOptions;
        variant?: NavbarVariant;
        isCurrent?: boolean;
    }>
>(function NavbarLink({ id, link, variant = 'dark', isCurrent, children }, ref) {
    const canOpen = useCanOpen();
    const isLinkActive = useIsRouteActive(link, { exact: false });
    if (!canOpen(link)) {
        return null;
    }
    return (
        <NavbarAnchor ref={ref} id={id} {...link} variant={variant} isCurrent={isCurrent ?? isLinkActive}>
            {children}
        </NavbarAnchor>
    );
});

/** Current on every page under `/parts`, though it links to the search. */
function PartsNavbarLink(): JSX.Element {
    const isCurrent = useIsRouteActive({ to: '/parts' });
    return (
        <NavbarLink link={homeLinks.parts} isCurrent={isCurrent}>
            <Trans>Parts</Trans>
        </NavbarLink>
    );
}

/** `/templates` and `/manufacturing` are no pages, so each page the Templates entry stands for is asked about. */
function useIsTemplatesActive(): boolean {
    return [
        useIsRouteActive({ to: '/templates/sourcing' }),
        useIsRouteActive({ to: '/templates/quote-request' }),
        useIsRouteActive({ to: '/templates/calculation' }),
        useIsRouteActive({ to: '/templates/email' }),
        useIsRouteActive({ to: '/templates/quotation' }),
        useIsRouteActive({ to: '/templates/export' }),
        useIsRouteActive({ to: '/templates/pcb-specification' }),
        useIsRouteActive({ to: '/templates/flexible-calculation' }),
        useIsRouteActive({ to: '/manufacturing/activities' }),
        useIsRouteActive({ to: '/manufacturing/drivers' }),
        useIsRouteActive({ to: '/manufacturing/expenses' }),
        useIsRouteActive({ to: '/manufacturing/resources' }),
        useIsRouteActive({ to: '/manufacturing/templates/{-$templateId}' }),
        // Any app's tab. Without `params` the route matches every app and widget, which the link types cannot say.
        useIsRouteActive({ to: '/manufacturing/apps/$appId/$widgetId' } as unknown as AppLinkOptions),
    ].some(Boolean);
}

/** Links to the first template page the user may open, so one without the sourcing templates still gets the entry. */
function TemplatesNavbarLink(): JSX.Element | null {
    const isCurrent = useIsTemplatesActive();
    const link = Object.values(templateLinks).find(useCanOpen());
    if (link === undefined) {
        return null;
    }
    return (
        <NavbarLink link={link} isCurrent={isCurrent}>
            <Trans>Templates</Trans>
        </NavbarLink>
    );
}

export const HomeNavbar = React.memo(function HomeNavbar(): JSX.Element {
    useCustomFavicon();
    useCustomTitle();
    const { userType, organization } = useCurrentUserDetailsContext();
    const featureFlags = useFeatureFlag();
    const hasCustomers = organization.has_customers;
    return (
        <NavbarContainer>
            <NavDark data-testid="NavDark">
                <LogoLink to="/">
                    <NavbarIconLight />
                </LogoLink>

                {/* Customers on hosts without customer RfQs work from Assemblies and Orders, so they don't see this
                    entry. The list stays open by URL on purpose. */}
                {(!isCustomerRfQDisabled() || userType !== UserType.Customer) && (
                    <NavbarLink link={homeLinks.projects}>
                        <Trans>Projects</Trans>
                    </NavbarLink>
                )}

                <NavbarLink link={homeLinks.assemblies}>
                    <Trans>Assemblies</Trans>
                </NavbarLink>

                <NavbarLink link={homeLinks.orders}>
                    <Trans>Orders</Trans>
                </NavbarLink>

                <PartsNavbarLink />

                <NavbarLink link={homeLinks.risk}>
                    <Trans>Risk</Trans>
                </NavbarLink>

                <TemplatesNavbarLink />

                <NavbarLink link={homeLinks.customers}>
                    {hasCustomers ? <Trans>Customers</Trans> : <Trans>Companies</Trans>}
                </NavbarLink>
                <NavbarLink link={homeLinks.manufacturers}>
                    <Trans>Manufacturers</Trans>
                </NavbarLink>
                <NavbarLink link={homeLinks.suppliers}>
                    <Trans>Suppliers</Trans>
                </NavbarLink>
                <NavbarLink link={homeLinks.purchasing}>
                    <Trans>Purchasing</Trans>
                </NavbarLink>
                {/* The add-on gates the entry; the pages stay open, as the Insights agent links into them. */}
                {(!isProductionEnvironment() || featureFlags.negotiations) && (
                    <NavbarLink link={homeLinks.negotiations}>
                        <Trans>Negotiations</Trans>
                    </NavbarLink>
                )}
                <NavbarLink link={homeLinks.insights}>
                    <Trans>Insights</Trans>
                </NavbarLink>
                {/* A developer shortcut; suppliers arrive by email and from `/`. */}
                {!isProductionEnvironment() && (
                    <NavbarLink link={homeLinks.supplierPortal}>
                        <Trans>Supplier portal</Trans>{' '}
                        <span
                            style={{
                                marginLeft: 4,
                                background: colorSystem.primary[7],
                                color: colorSystem.neutral.white,
                                padding: ' 2px 4px',
                                borderRadius: '4px',
                            }}
                        >
                            dev
                        </span>
                    </NavbarLink>
                )}
                {<Filler />}
                <GlobalCommandPalette telemetryLocation="navbar" variant="dark" />
                <NavbarNotifications variant="dark" />
                <Avatar variant={'dark'} />
            </NavDark>
        </NavbarContainer>
    );
});

const DotSeparator = styled(Box)({
    background: colorSystem.neutral[5],
    width: 4,
    height: 4,
    marginLeft: 4,
    marginRight: 4,
    borderRadius: '100%',
});

function RfqSection({ rfq, customer }: { rfq: RfqDTO; customer: CustomerDTO }): JSX.Element {
    return (
        <>
            <Protected requiredPermissions={['view:rfq:details']}>
                <>
                    <CustomerLabel customerName={customer.name} customerNumber={customer.customer_number} />
                    <DotSeparator />
                </>
            </Protected>
            <RfqLabel rfqName={rfq.name} emsInternalNumber={rfq.ems_internal_number ?? undefined} />
            <Flexbox
                gap={8}
                sx={{
                    marginLeft: 1,
                }}
            >
                <StyledRfqStatusBox rfqStatus={rfq.status} />
                {rfq.is_archived && <StyledRfqIsArchivedBox />}
            </Flexbox>
        </>
    );
}

const StyledRfqIsArchivedBox = (): JSX.Element => {
    return <Tag color="red" label={t`Archived`} />;
};

/** `projectId` is a prop, from the match that picked this navbar; reading params here could load another project. */
export const ProjectNavbar = React.memo(function ProjectNavbar({ projectId }: { projectId: string }): JSX.Element {
    useCustomTitle();
    useCustomFavicon();

    const links = projectLinks(projectId);
    const { data: rfq } = useRfQ(projectId);
    const { data: customer } = useCustomer(rfq?.customer);

    return (
        <>
            <GlobalCommandPalette telemetryLocation="rfq-navbar" variant="light" showInput={false} />

            <NavbarContainer>
                <NavLight data-testid="NavLight">
                    <LogoLink to="/" style={{ display: 'flex', flexDirection: 'row' }}>
                        <HouseIcon size={20} color={colorSystem.neutral[9]} />
                        <NavbarIconDark />
                    </LogoLink>
                    <NavbarLink id={id('navbar/button_dashboard')} link={links.dashboard} variant="light">
                        <Trans>Dashboard</Trans>
                    </NavbarLink>
                    <NavbarLink id={id('navbar/button_design')} link={links.design} variant="light">
                        <Trans>Design</Trans>
                    </NavbarLink>
                    <NavbarLink link={links.sourcing} variant="light">
                        <Trans>Sourcing</Trans>
                    </NavbarLink>
                    <NavbarLink id={id('navbar/link_manufacturing')} link={links.manufacturing} variant="light">
                        <Trans>Manufacturing</Trans>
                    </NavbarLink>
                    <NavbarLink link={links.calculation} variant="light">
                        <Trans>Calculation</Trans>
                    </NavbarLink>
                    <NavbarLink link={links.flexibleCalculation} variant="light">
                        <Trans>Flexible calculation</Trans>
                    </NavbarLink>
                    <NavbarLink link={links.quotation} variant="light">
                        <Trans>Quotation</Trans>
                    </NavbarLink>
                    <Filler />

                    {rfq && customer && <RfqSection rfq={rfq} customer={customer} />}
                    <DotSeparator style={{ marginInline: '12px' }} />
                    <NavbarNotifications variant="light" rfqId={projectId} />
                    <Avatar variant={'light'} />
                </NavLight>
            </NavbarContainer>
        </>
    );
});
